import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, hasRole } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProductSelect } from "../components/ProductSelect";
import { AddCustomerDialog } from "../components/AddCustomerDialog";

const emptyItem = () => ({ _key: crypto.randomUUID(), product_name: "", quantity: "" });

export default function NewOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customerName, setCustomerName] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [addingCustomer, setAddingCustomer] = useState(false);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const onCustomerChange = (v) => {
    setCustomerName(v);
    const match = customers.find((c) => c.name.toLowerCase() === v.toLowerCase());
    if (match) setLocation(match.location);
  };

  const setItem = (i, key, value) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  };

  const totalQty = items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const parsed = items.map((it) => ({
      product_name: it.product_name.trim(),
      quantity: parseFloat(it.quantity),
    }));
    if (!customerName) {
      toast.error("Select a customer from the list");
      return;
    }
    if (parsed.some((it) => !it.product_name || !(it.quantity > 0))) {
      toast.error("Every line item needs a product name and quantity > 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: customerName,
        location,
        items: parsed,
        notes: notes || null,
      });
      toast.success(`Order ${data.order_number} created`);
      navigate(`/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="new-order-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">New Order</h1>
        <p className="mt-1 text-sm text-slate-500">Fast entry — customer, location and line items.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="grid gap-4 rounded-md border border-slate-200 bg-white p-5 sm:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer *</label>
              {(hasRole(user, "admin") || hasRole(user, "sales") || hasRole(user, "sales_lead")) && (
                <button type="button" data-testid="new-customer-button" onClick={() => setAddingCustomer(true)}
                  className="text-xs font-medium text-slate-600 underline-offset-4 hover:underline">
                  + Add new customer
                </button>
              )}
            </div>
            <ProductSelect products={customers} value={customerName} testId="order-customer-input"
              allowCustom={false} placeholder="Select customer…"
              onChange={onCustomerChange} />
            {!customerName && (
              <p className="mt-1 text-[11px] text-slate-400">Only customers from the master list can be selected — keeps the master clean.</p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location *</label>
            <input data-testid="order-location-input" required value={location}
              onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Pune, Maharashtra"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes (optional)</label>
            <textarea data-testid="order-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} placeholder="Any special instructions…"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Line Items</h2>
            <button type="button" data-testid="add-line-item-button"
              onClick={() => setItems((p) => [...p, emptyItem()])}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100">
              <Plus size={14} weight="bold" /> Add item
            </button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={it._key} className="grid grid-cols-12 items-end gap-2" data-testid={`line-item-row-${i}`}>
                <div className="col-span-12 sm:col-span-8">
                  {i === 0 && <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</label>}
                  <ProductSelect products={products} value={it.product_name} testId={`item-product-input-${i}`}
                    allowCustom={hasRole(user, "admin")}
                    onChange={(v) => setItem(i, "product_name", v)} />
                </div>
                <div className="col-span-8 sm:col-span-3">
                  {i === 0 && <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty</label>}
                  <input data-testid={`item-quantity-input-${i}`} type="number" min="0" step="any" value={it.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="0"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-right font-mono text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
                </div>
                <div className="col-span-1 pb-1">
                  {items.length > 1 && (
                    <button type="button" data-testid={`remove-item-button-${i}`}
                      onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}
                      className="text-slate-400 transition-colors duration-150 hover:text-red-600">
                      <Trash size={16} weight="duotone" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-8 border-t border-slate-200 pt-3 text-sm">
            <p className="text-slate-500">Total qty: <span className="font-mono font-semibold text-slate-900" data-testid="order-total-qty">{totalQty}</span></p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button data-testid="submit-order-button" type="submit" disabled={saving}
            className="w-full rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60 sm:w-auto sm:py-2.5">
            {saving ? "Creating…" : "Create Order"}
          </button>
          <button type="button" data-testid="cancel-order-button" onClick={() => navigate("/orders")}
            className="w-full rounded-md border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 sm:w-auto sm:py-2.5">
            Cancel
          </button>
        </div>
      </form>
      {addingCustomer && (
        <AddCustomerDialog onClose={() => setAddingCustomer(false)}
          onAdded={(c) => { setCustomers((p) => [...p, c]); setCustomerName(c.name); setLocation(c.location); setAddingCustomer(false); }} />
      )}
    </div>
  );
}
